import {
    BackgroundImageDisplayType,
    ButtonActionType,
    ButtonNavigationType,
    ButtonPositionType,
    CarouselArrowType,
    CollectionItemType,
    HeroLayoutType,
    MarketingBannerType,
    MobileBuyBannerType,
    ProductCardType,
    RoundedCornerSize,
    SiteStyleType,
    TextAlignment
} from "./enums";
import { Asset, Collection, Facet, FacetValue, Page, Product, ProductVariant, ShippingMethodQuote } from "./generated-types";
import * as fabric from 'fabric';

export type NavigationTargetType = string;

export interface NavigationItem {
    label: string;
    targetType: NavigationTargetType;
    pageSlug?: string;
    pageSection?: string;
    builtInPage?: string;
    customSlug?: string;
    websiteUrl?: string;
    targetProductId?: string | null;
    targetProductSlug?: string;
    targetCollectionId?: string | null;
    targetCollectionSlug?: string;
}

export interface SiteRoute {
    slug: string;
    name: string;
    type: string;
}

export interface LogoSectionData {
    pageTitle: string;
    displayHeaderMenu: boolean;
    displayFooterMenu: boolean;
    normalLogo: Asset | null;
    footerLogo?: Asset | null;
    smallLogo: Asset | null;
    nameLogo: Asset | null;
    browserIcon: Asset | null;
    webclipIcon: Asset | null;
    displayName: boolean;
}

export interface KeySellingPointData {
    icon: Asset | null;
    title: string;
    value: string;
}

export interface BasicCardData {
    asset: Asset | null;
    title: string;
    text: string;
}

export interface Review {
    name: string;
    rating: number;
    content?: string;
    summary: string;
    featuredAsset?: Asset | null;
}

export interface ButtonData {
    enabled: boolean;
    text: string;
    textSize: number;
    mobileTextSize: number;
    textColor: string;
    backgroundColor: string;
    actionType: ButtonActionType;
    selectedVariantId: string;
    isVariantCustomizable?: boolean;
    navigationItem?: NavigationItem;
    navigateType?: ButtonNavigationType;
    targetProduct: Product | null;
    targetCollection: Collection | null;
    targetPage?: string;
    targetPageSectionId?: string;
}

export interface EmailSubscriptionData {
    type: string;
    backgroundColor: string;
    backgroundImage: Asset | null;
    shadowTransparencyValue: number;
    omnisendFormId: string;
}

export interface HeroSectionData {
    layoutType: HeroLayoutType;
    medias: Asset[];
    mobileMedias: Asset[];
    title: string;
    subtitle: string;
    textColor: string;
    textPositionX?: number | null;
    textPositionY?: number | null;
    mobileTextPositionX?: number | null;
    mobileTextPositionY?: number | null;
    textWidth?: number | null;
    titleFontSize: number;
    subtitleFontSize: 20,
    mobileTitleFontSize: 24,
    mobileSubtitleFontSize: 18,
    shadowTransparencyValue: number;
    button: ButtonData;
    buttonPosition: ButtonPositionType;
    buttonAlignment: TextAlignment;
    buttonLocationX: number;
    buttonLocationY: number;
    buttonPositionMobile: ButtonPositionType;
    buttonAlignmentMobile: TextAlignment;
    buttonLocationXMobile: number;
    buttonLocationYMobile: number;
}

export interface ExternalHtmlContentData {
    htmlContent: string;
    medias: Asset[];
    mobileMedias: Asset[];
}

export interface FaqListData {
    defaultDisplayCount: number;
    qaList: QAData[];
}

export interface KeyPointListData {
    iconSize: number;
    titleFontSize: number;
    subtitleFontSize: number;
    textFontSize: number;
    titleAlignment: string;
    textAlignment: string;
    backgroundColor: string;
    textColor: string;
    keyPointsLayoutType: string;
    mobileKeyPointsLayoutType: string;
    keyPoints: KeySellingPointData[];
}

export interface CardCarouselBanner {
    titleFontSize: number;
    subtitleFontSize: number;
    textFontSize: number;
    titleAlignment: string;
    textAlignment: string;
    basicCardShape: string;
    slideNumber: number;
    mobileStyle: string;
    backgroundColor: string;
    textColor: string;
    mediaRoundedCorners?: RoundedCornerSize;
    basicCards: BasicCardData[];
}

export interface ProductListingData {
    productListingType: string;
    collection: Collection | null;
    facetValues: FacetValue[];
}

export interface ProductInfoData {
    product?: Product | null;
    productVariant?: ProductVariant | null;
}

export interface SimpleProductInfoData {
    productVariant: ProductVariant;

    displayThumbnails: boolean;
    displayFeaturedReview: boolean;
    displayProductSubtitle: boolean;
    displaySku: boolean;
    displayReviewInfo: boolean;
    displayPaymentCapabilities: boolean;
    displayProductOptions: boolean;
    displayProductDescription: boolean;
    displayAddToCart: boolean;
    displayProductKeyPoints: boolean;
    displayProductFeatures: boolean;
    displayProductPageEntry: boolean;
}

export interface MarketingBannerData {
    type: MarketingBannerType;
    mediaHeight?: number;
    mediaRoundedCorners?: RoundedCornerSize;
    medias: Asset[];
    mobileMedias: Asset[];
    mobileMediaPosition: string;
    mobileMediaStyle: string;
    textBarStyle: string;
    leftRightPartRatio: number;
    mediaTextGap: number;
    shadowTransparencyValue: number;
    backgroundMedia: Asset | null;
    backgroundMobileMedia: Asset | null;
    autoSlideDelay: number;
    layoutStyle: string;
    backgroundColor: string;
    textColor: string;
    title: string;
    text: string;
    titleFontSize: number;
    textFontSize: number;
    textAlignment: string;
    textWidth: number;
    textIndentation: number;
    titleIndentation: number;
    secColTitle: string;
    secColText: string;
    secColTitleFontSize: number;
    secColTextFontSize: number;
    secColTextAlignment: string;
    secColTextWidth: number;
    secColTitleIndentation: number;
    secColTextIndentation: number;
    button: ButtonData;
    buttonPosition: ButtonPositionType;
    buttonAlignment: TextAlignment;
    buttonLocationX: number;
    buttonLocationY: number;
    buttonPositionMobile: ButtonPositionType;
    buttonAlignmentMobile: TextAlignment;
    buttonLocationXMobile: number;
    buttonLocationYMobile: number;
    width?: number;
}


export type PageComponentContent =
    | MarketingBannerData
    | KeyPointListData
    | CardCarouselBanner
    | HeroSectionData
    | ExternalHtmlContentData
    | FaqListData
    | FeaturedCollectionData
    | CollectionGroupData
    | TopReviewsData
    | EmailSubscriptionData
    | ProductInfoData
    | ProductListingData
    | ProductReviewsData
    | CollectionCarouselData
    | AboutFormData
    | ContactFormData;

export type PageComponentTitleMode = 'disabled' | 'for-section' | 'for-component';

export interface TitleInfo {
    title: string;
    subtitle: string;
    titleUnderline: boolean;
    titleMode: PageComponentTitleMode;
    isPageHeading?: boolean;
    h1StyleMode?: string;
}

export interface PageComponentData {
    type: string;
    sectionId: string;
    titleMode: PageComponentTitleMode;
    title?: string;
    subtitle?: string;
    titleUnderline?: boolean;
    layoutOptions?: LayoutOptionData;
    content: PageComponentContent;
}

export interface TopReviewsData {
    reviews: ProductReview[];
}

export interface ProductReview {
    id: string;
    updatedAt: string;
    summary: string;
    body: string;
    rating: number;
    authorName: string;
    featuredAsset?: Asset | null;
}

export interface ProductReviewsData {
    backgroundColor: string;
    product: Product;
}

export interface ImageFrameData {
    name: string;
    label: string;
    labelColor: string;
    labelFontSize: number;
    positionX: number;
    positionY: number;
    sizeX: number;
    sizeY: number;
    /**
     * Nested text-overlay groups owned by this image frame. When present, each
     * overlay's drag/resize boundary is this frame's rect rather than the full
     * canvas. Standalone overlays from a sibling `custom-text-frames` option
     * leave this undefined.
     */
    textFrameGroups?: TextFrameGroupConfig[];
}

export interface ProductCustomizationMessageOption {
    optionId:   string;
    label: string;
    description: string;
    required: boolean;
}

export interface ProductCustomizationPaidOption {
    optionId:   string;
    label: string;
    description: string;
    required: boolean;
    showImage: boolean;
    displayType: string;
    options: PaidOptionItem[];
}

export interface PaidOptionItem {
    label: string;
    key: string;
    description: string;
    asset: Asset | null;
    price: number;
}

export interface LabeledImage {
    label: string;
    asset: Asset | null;
}

export interface TextFrameData {
    name: string;
    label?: string;
    maxLength: number;
    maxLines?: number;
    required: boolean;
    textAlign: 'left' | 'center' | 'right';
}

export interface TextFrameColorOption {
    hex: string;
    name: string;
}

export function normalizeTextFrameColor(c: string | TextFrameColorOption): TextFrameColorOption {
    return typeof c === 'string' ? { hex: c, name: '' } : c;
}

export interface TextFrameGroupConfig {
    name: string;
    allowedFonts: string[];
    allowedColors: TextFrameColorOption[];
    defaultFont: string;
    defaultColor: string;
    autoFit: boolean;
    fontSizeOptions: number[];
    defaultFontSize?: number;
    frames: TextFrameData[];
}

export interface ProductCustomizationPrintTemplate {
    optionId:   string;
    label: string;
    description: string;
    minWidth: number;
    minHeight: number;
    canvasWidth: number;
    canvasHeight: number;
    backgroundImageDisplayType: BackgroundImageDisplayType;
    backgroundImages: LabeledImage[];
    imageFrames: ImageFrameData[];
}

export interface ProductFeatureData {
    title: string;
    text: string;
}

export interface CollectionGroupData {
    groupName: string;
    description: string;
    backgroundAsset: Asset | null;
    featuredAssets: Asset[];
    collections: Collection[];
}

export interface ColorConfig {
    mainColor: string;
    mainTextColor: string;
    secondaryColor: string;
    secondaryTextColor: string;
    headerBackgroundColor: string;
    headerFontColor: string;
    footerBackgroundColor: string;
    footerFontColor: string;
}

export interface SnsInfo {
    instagram: string;
    facebook: string;
    xTwitter: string;
    youtube: string;
}

export interface LayoutOptionData {
    width: number;
    widthPercentage?: string;
    mobileWidth: number;
    mobileWidthPercentage?: string;
    topMargin: number;
    bottomMargin: number;
    mobileTopMargin: number;
    mobileBottomMargin: number;
    styles?: { [key: string]: string };
    horizontalPadding?: number;
    topPadding?: number;
    bottomPadding?: number;
    mobileHorizontalPadding?: number;
    mobileTopPadding?: number;
    mobileBottomPadding?: number;
    tailwindClasses?: string;
    mobileTailwindClasses?: string;
    linePadding?: string;
    sectionRoundedCorners?: RoundedCornerSize;
    mobileSectionRoundedCorners?: RoundedCornerSize;
    roundedClass?: string;
    pageFullWidth?: boolean;
    sectionMaxHeight?: string;
}

export type NavMenuOption = NavigationItem & {
    // backward-compatible legacy fields
    type?: string;
    url?: string;
    section?: string;
};

export interface PolicyContentData {
    type: string;
    title: string;
    path: string;
    content: string;
}

export type HeaderMessageDisplayOrderType = 'sequence' | 'random';
export type HeaderMessageSwitchType = 'auto' | 'manual';
export interface HeaderMessageConfig {
    displayOrder: HeaderMessageDisplayOrderType;
    switchType: HeaderMessageSwitchType;
    autoSwitchInterval: number;
    messageList: string[];
}

export interface SiteStyleData {
    siteTitle?: string;
    displayHeaderMenu?: boolean;
    displayFooterMenu?: boolean;
    displayStoreName?: boolean;
    displayHeaderMessage?: boolean;
    copyrightNotice?: string;
    disclaimer?: string;
    pdpAssetsArrowType?: CarouselArrowType;
    mobileBuyBannerType?: MobileBuyBannerType;
}

export interface QAData {
    question: string;
    answer: string;
    open?: boolean;
}

export interface ReturnCancelPolicyData {
    enableCancel: boolean;
    cancellableTimeInHours: number;
    cancelExpiredNotification: string;
    enableReturn: boolean;
    returnableTimeInDays: number;
    returnExpiredNotification: string;
    disabledPolicyNotification: string;
}

export interface CollectionItemOrderData {
    collectionId: string;
    enabled: boolean;
    orderType: CollectionItemType;
    variantIdsInOrder: string[];
    productIdsInOrder: string[];
}

export interface FeaturedCollectionData {
    collection: Collection | null | undefined;
    displayType: CollectionItemType;
    productCardType: ProductCardType;
    productCardRoundedCorners?: RoundedCornerSize;
    buttonText?: string | null;
    buttonColorMode?: string;
    buttonBgColor?: string | null;
    buttonTextColor?: string | null;
}

export interface CollectionCarouselData {
    textAlignment: string;
    mobileStyle: string;
    collections: Collection[];
}

export interface AboutFormData {
    text: string;
    citation: string;
    featuredAsset: Asset | null;
}

export interface ContactFormData {
    contactFormType: string;
    email: string;
    featuredAsset: Asset | null;
}

export interface ExternalServicesSettings {
    omnisend: OmnisendSettings;
    gtag: GtagSettings;
    clarity: ClaritySettings;
    tawkio: TawkioSettings;
}

export interface OmnisendSettings {
    enabled: boolean;
    brandId: string;
}

export enum GtagMode {
    SERVER_SIDE = 'SERVER_SIDE',
    CLIENT_SIDE = 'CLIENT_SIDE'
}
export interface GtagSettings {
    enabled: boolean;
    trackingId: string;
    mode: GtagMode;
}

export interface ClaritySettings {
    enabled: boolean;
    trackingId: string;
}

export interface TawkioSettings {
    enabled: boolean;
    chatId?: string;
    embedChatId?: string;
}

export interface TextAssetConfig {
    label: string;
    content?: string;
    required?: boolean;
}

export interface ImageAssetConfig {
    position: { x: number; y: number };
    dimension: { width: number; height: number };
    scale: { scaleX: number; scaleY: number };
    angleOfRotation: number;
}

export interface PreviewContainerAssetConfig {
    dimension: { width: number; height: number };
}

export interface PaidOptionAssetConfig {
    label: string;
    content?: string;
    key: string;
    required?: boolean;
}

export interface TextFrameAssetConfig {
    groupName: string;
    /**
     * Name of the owning ImageFrame for nested text-overlay groups. Undefined
     * for standalone overlays from a `custom-text-frames` option.
     */
    imageFrameName?: string;
    position: { x: number; y: number };
    dimension: { width: number; height: number };
    scale: { scaleX: number; scaleY: number };
    angleOfRotation: number;
    content: string;
    fontFamily: string;
    color: string;
    colorName?: string;
    textAlign: 'left' | 'center' | 'right';
}

export type CustomizableAssetConfig = TextAssetConfig | ImageAssetConfig | PreviewContainerAssetConfig | PaidOptionAssetConfig | TextFrameAssetConfig;

export interface CustomizableAsset {
    type: string;
    name: string;
    config: CustomizableAssetConfig | null;
    text: string;
    imageKey: string;
    thumbnailImageKey: string;
    amount: number;
}

export interface ProductCustomizationTextFramesData {
    label: string;
    description: string;
    textFrameGroups: TextFrameGroupConfig[];
}

export interface ProductCustomizationOption {
    id: string;
    name: string;
    code: string;
    type: string;
    previewAsset: Asset | null;
    printTemplate: ProductCustomizationPrintTemplate | null;
    message: ProductCustomizationMessageOption | null;
    paidOption: ProductCustomizationPaidOption | null;
    textFrames: ProductCustomizationTextFramesData | null;
}
export interface FrameState {
    frameIndex: number;
    clipRect: fabric.Rect;
    frameRect: fabric.Rect;
    image: fabric.Image | null;
    name: string;
    label: string;
    backgroundImage?: fabric.Image | null;
    uploadedImageUrl: string | null;
    originalImageDataUrl?: string;
    thumbnailUploadUrl?: string | null;
    isLoading: boolean;
    fileName: string | null;
    customAsset: CustomizableAsset | null;
}

export interface TextFrameState {
    frameIndex: number;
    frameData: TextFrameData;
    textbox: fabric.FabricText;
    content: string;
    displayText: string;
    isValid: boolean;
    isOverLineLimit: boolean;
}

export interface TextFrameGroupState {
    groupIndex: number;
    config: TextFrameGroupConfig;
    sourceOptionId: string;
    selectedFont: string;
    selectedColor: string;
    selectedColorName: string;
    selectedFontSize: number;
    effectiveFonts: { family: string; googleFontsUrl: string }[];
    frameStates: TextFrameState[];
    /**
     * For standalone (`custom-text-frames`) overlays: the owning option's label
     * and description, used as the section heading so it's consistent with other
     * customization options. Undefined for nested overlays (they use the frame
     * subheading instead).
     */
    sourceOptionLabel?: string;
    sourceOptionDescription?: string;
    /**
     * Name of the owning ImageFrame for nested overlays. When set, the
     * per-overlay boundary is the frame's clipRect rather than the full canvas.
     * Standalone overlays from a `custom-text-frames` sibling option leave this
     * undefined.
     */
    parentImageFrameName?: string;
    /**
     * frameIndex into ImageCustomizerModalComponent.frameStates for the owning
     * frame. Cached at init time so per-event handlers can look up the frame's
     * current clipRect without a name-scan.
     */
    parentImageFrameIndex?: number;
}

export interface ImageCustomizerOutput {
    previewImageUrl: string | null;
    canvasWidth: number;
    canvasHeight: number;
    frames: Array<{
        uploadedImageUrl: string | null;
        positionX: number;
        positionY: number;
        scaleX: number;
        scaleY: number;
        angle: number;
        originalFrameData: ImageFrameData;
    }>;
    customizationNotes?: string;
}

export interface CreateCustomizableImageUploadUrlsResult {
    imageKey: string;
    thumbnailImageKey: string;
    imageUploadUrl: string;
    thumbnailUploadUrl: string;
}

export interface ShippingRate {
    objectId: string;
    amount: number;
    durationTerms: string;
    estimatedDays: number;
    provider: string;
    shipment: string;
    serviceLevelName: string;
    selected?: boolean;
}

export interface DisplayableShippingOption {
    shippingMethodId: string;
    objectid: string;
    name: string;
    description: string;
    price: number;
    isRate: boolean;
    originalRate?: ShippingRate;
    originalMethod: ShippingMethodQuote;
    channel?: string;
    selected?: boolean;
  }
